/**
 * Guide Me — floating "ask the orb" launcher.
 *
 * Visual: a circular orb anchored to the bottom-right of the viewport, ringed
 * by a rotating multicolor halo and capped with a soft violet glow. Above the
 * orb a violet speech bubble cycles through one-line teasers of what the user
 * can learn on this screen, typed character-by-character with a blinking
 * cursor. The bubble's tail points down at the orb so the relationship reads
 * at a glance.
 *
 * Click the orb (or press Enter/Space when focused) to open the full menu.
 * When {@link HintPillProps.onDismissLauncher} is set, the speech bubble shows
 * a dismiss control that hides the launcher without opening the menu.
 */

import { useEffect, useMemo, useState, useCallback } from 'react';
import { applyLocale, pickTourLocaleKey } from '@empowernow/etl-tour';
import { useGuideMeAdapter } from './context';
import type { TourSummary } from './types';

export interface HintPillProps {
  /**
   * Lightweight summary from the lazy catalog. The body (steps, locales)
   * is **not** required to render the pill; if it has already been loaded
   * by an intent trigger (route preload), we'll opportunistically pick
   * the localized title from the cache.
   */
  tour: TourSummary;
  /** Optional host route segment for diagnostics or future use. */
  routeKey?: string;
  onLaunch: () => void;
  /** When provided, a close control on the teaser bubble dismisses the floating launcher. */
  onDismissLauncher?: () => void;
  /** Experience shell `resolvedLocale`. Used only when the tour body is cached. */
  appLocale: string;
  /** "tour" shows the standard launcher pill. "ask" shows an Ask-the-guide
   *  variant for routes without an authored tour. */
  variant?: 'tour' | 'ask';
  /**
   * Optional extra teasers shown above the orb in a cycling speech bubble.
   * Defaults to `[tour.title]`. When the parent has access to a richer set of
   * authored tours for the plugin, pass their localized titles here so the
   * user previews everything they can learn before opening the menu.
   */
  messages?: string[];
}

const TYPING_SPEED_MS = 55;
const DELETING_SPEED_MS = 22;
const HOLD_AFTER_TYPED_MS = 1800;

export function HintPill({
  tour,
  onLaunch,
  appLocale,
  variant = 'tour',
  messages,
  onDismissLauncher,
}: HintPillProps) {
  const adapter = useGuideMeAdapter();
  const handleDismissLauncher = useCallback(
    (ev: React.MouseEvent | React.KeyboardEvent) => {
      ev.stopPropagation();
      ev.preventDefault();
      onDismissLauncher?.();
    },
    [onDismissLauncher],
  );
  const handleLaunch = useCallback(
    (ev: React.MouseEvent | React.KeyboardEvent) => {
      ev.stopPropagation();
      ev.preventDefault();
      onLaunch();
    },
    [onLaunch],
  );

  const pillTitle = useMemo(() => {
    const cached = adapter.getCachedTour(tour.id);
    if (!cached) return tour.title;
    return applyLocale(cached, pickTourLocaleKey(appLocale, cached.locales)).title;
  }, [adapter, tour.id, tour.title, appLocale]);

  const teaserList = useMemo<string[]>(() => {
    const provided = (messages ?? []).map((m) => m.trim()).filter(Boolean);
    if (provided.length > 0) return provided;
    const fallback = variant === 'ask' ? 'Ask the guide' : pillTitle || 'How it works?';
    return [fallback];
  }, [messages, pillTitle, variant]);

  /* ─────────────── typing-bubble cycler ─────────────── */
  const [msgIdx, setMsgIdx] = useState(0);
  const [typed, setTyped] = useState('');
  const [phase, setPhase] = useState<'typing' | 'holding' | 'deleting'>('typing');

  useEffect(() => {
    setMsgIdx(0);
    setTyped('');
    setPhase('typing');
  }, [teaserList]);

  useEffect(() => {
    if (teaserList.length === 0) return;
    const current = teaserList[msgIdx % teaserList.length];

    if (phase === 'typing') {
      if (typed.length < current.length) {
        const t = window.setTimeout(
          () => setTyped(current.slice(0, typed.length + 1)),
          TYPING_SPEED_MS,
        );
        return () => window.clearTimeout(t);
      }
      if (teaserList.length === 1) return;
      const t = window.setTimeout(() => setPhase('holding'), HOLD_AFTER_TYPED_MS);
      return () => window.clearTimeout(t);
    }

    if (phase === 'holding') {
      setPhase('deleting');
      return;
    }

    if (typed.length > 0) {
      const t = window.setTimeout(
        () => setTyped(typed.slice(0, -1)),
        DELETING_SPEED_MS,
      );
      return () => window.clearTimeout(t);
    }
    setMsgIdx((i) => (i + 1) % teaserList.length);
    setPhase('typing');
    return;
  }, [typed, phase, msgIdx, teaserList]);

  return (
    <div
      data-testid="guide-me-pill"
      className="gm-surface"
      role="region"
      aria-label="Guided tour available for this page"
      style={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        zIndex: 99800,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: 14,
        fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
        animation: 'guideMePillIn 280ms cubic-bezier(0.34,1.56,0.64,1) both',
      }}
    >
      {/* Speech bubble — sits above the orb, violet gradient to match the launcher */}
      <div
        className="guide-me-bubble"
        aria-live="polite"
        style={{
          position: 'relative',
          maxWidth: 280,
          padding: onDismissLauncher ? '12px 36px 12px 18px' : '12px 18px',
          background: 'var(--gm-bubble)',
          color: 'var(--gm-text)',
          border: '1px solid var(--gm-border)',
          borderRadius: 18,
          fontSize: 14,
          fontWeight: 500,
          lineHeight: 1.35,
          boxShadow: 'var(--gm-bubble-shadow)',
          letterSpacing: 0.1,
          minHeight: 20,
        }}
      >
        {onDismissLauncher ? (
          <button
            type="button"
            data-testid="guide-me-pill-dismiss"
            aria-label="Dismiss Guide Me launcher"
            onClick={handleDismissLauncher}
            onKeyDown={(ev) => {
              if (ev.key === 'Enter' || ev.key === ' ') handleDismissLauncher(ev);
            }}
            style={{
              position: 'absolute',
              top: 6,
              right: 6,
              width: 28,
              height: 28,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: 'none',
              borderRadius: 8,
              padding: 0,
              cursor: 'pointer',
              background: 'var(--gm-accent-soft)',
              color: 'var(--gm-text)',
              fontSize: 18,
              lineHeight: 1,
              fontWeight: 600,
            }}
          >
            ×
          </button>
        ) : null}
        {/* `color: inherit` inline keeps these spans on the bubble's light
            text: an inline style beats the shell's non-!important
            `[data-theme] span { color: var(--main-text) }` rule, which would
            otherwise force dark text on this dark bubble. */}
        <span style={{ color: 'inherit' }}>{typed || ' '}</span>
        <span className="guide-me-bubble-cursor" aria-hidden="true" style={{ color: 'inherit' }}>
          _
        </span>
      </div>

      {/* Orb — rotating multicolor ring + glow + ? center */}
      <div
        className="guide-me-orb-wrapper"
        style={{ position: 'relative', width: 64, height: 64 }}
      >
        <button
          type="button"
          data-testid="guide-me-pill-launch"
          aria-label={`Open Guide Me — ${pillTitle}`}
          onClick={handleLaunch}
          onKeyDown={(ev) => {
            if (ev.key === 'Enter' || ev.key === ' ') handleLaunch(ev);
          }}
          className="guide-me-orb"
          style={{
            width: 64,
            height: 64,
            border: 'none',
            padding: 0,
            background: 'transparent',
            cursor: 'pointer',
            display: 'block',
            position: 'relative',
            borderRadius: '50%',
          }}
        >
          {/* rotating conic-gradient ring */}
          <span className="guide-me-orb-ring" aria-hidden="true" />
          {/* solid inner disc with question-mark glyph */}
          <span className="guide-me-orb-core" aria-hidden="true">
            <span className="guide-me-orb-glyph">?</span>
          </span>
        </button>
      </div>
    </div>
  );
}

/** Inject the pill's keyframes once. */
export function ensureHintPillStyles(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById('guide-me-pill-styles')) return;
  const style = document.createElement('style');
  style.id = 'guide-me-pill-styles';
  style.textContent = `
    @keyframes guideMePillIn {
      0%   { opacity: 0; transform: translateY(12px) scale(0.92); }
      100% { opacity: 1; transform: translateY(0) scale(1); }
    }
    @keyframes guideMeOrbSpin {
      to { transform: rotate(360deg); }
    }
    @keyframes guideMeOrbGlow {
      0%, 100% { filter: drop-shadow(0 0 12px rgba(108, 76, 255, 0.35)); }
      50%      { filter: drop-shadow(0 0 20px rgba(108, 76, 255, 0.55)); }
    }
    @keyframes guideMeOrbBlob {
      0%, 100% { border-radius: 42% 58% 63% 37% / 45% 42% 58% 55%; }
      25%      { border-radius: 60% 40% 35% 65% / 55% 32% 68% 45%; }
      50%      { border-radius: 52% 48% 58% 42% / 38% 60% 40% 62%; }
      75%      { border-radius: 36% 64% 52% 48% / 60% 38% 62% 40%; }
    }
    @keyframes guideMeGlyphFloat {
      0%, 100% { transform: translateY(0) rotate(-6deg); }
      50%      { transform: translateY(-2px) rotate(6deg); }
    }
    @keyframes guideMeBubbleCursor {
      0%, 49%   { opacity: 1; }
      50%, 100% { opacity: 0; }
    }

    .guide-me-orb-wrapper { animation: guideMeOrbGlow 3.2s ease-in-out infinite; }
    .guide-me-orb:hover   { transform: scale(1.04); transition: transform 160ms ease-out; }
    .guide-me-orb-ring {
      position: absolute; inset: 0;
      background: conic-gradient(
        from 0deg,
        var(--quantum-violet, #6C4CFF) 0%,
        #8b5cf6 30%,
        #a78bfa 55%,
        #8b5cf6 78%,
        var(--quantum-violet, #6C4CFF) 100%
      );
      animation:
        guideMeOrbSpin 4s linear infinite,
        guideMeOrbBlob 9s ease-in-out infinite;
    }
    .guide-me-orb-core {
      position: absolute; inset: 3px;
      background:
        radial-gradient(circle at 30% 28%, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0) 60%),
        var(--card-bg, #ffffff);
      display: flex; align-items: center; justify-content: center;
      box-shadow:
        inset 0 1px 2px rgba(255, 255, 255, 0.9),
        inset 0 -2px 6px rgba(2, 6, 23, 0.08);
      animation: guideMeOrbBlob 9s ease-in-out infinite;
    }
    .guide-me-orb-glyph {
      font-family: system-ui, -apple-system, "Segoe UI", sans-serif;
      font-weight: 700;
      font-size: 28px;
      line-height: 1;
      color: var(--quantum-violet, var(--main-accent, #6C4CFF));
      -webkit-text-fill-color: var(--quantum-violet, var(--main-accent, #6C4CFF));
      user-select: none;
      display: inline-block;
      transform-origin: 50% 60%;
      animation: guideMeGlyphFloat 2.4s ease-in-out infinite;
    }
    .guide-me-bubble::after {
      content: '';
      position: absolute;
      bottom: -7px;
      right: 24px;
      width: 14px;
      height: 14px;
      background: var(--gm-bubble);
      border-right: 1px solid var(--gm-border);
      border-bottom: 1px solid var(--gm-border);
      transform: rotate(45deg);
      border-bottom-right-radius: 3px;
    }
    .guide-me-bubble-cursor {
      display: inline-block;
      margin-left: 2px;
      animation: guideMeBubbleCursor 1s steps(1) infinite;
    }

    @media (prefers-reduced-motion: reduce) {
      [data-testid="guide-me-pill"] { animation: none !important; }
      .guide-me-orb-wrapper,
      .guide-me-orb-ring,
      .guide-me-orb-core,
      .guide-me-orb-glyph,
      .guide-me-bubble-cursor {
        animation: none !important;
      }
      .guide-me-orb-ring,
      .guide-me-orb-core {
        border-radius: 42% 58% 63% 37% / 45% 42% 58% 55%;
      }
      .guide-me-orb-wrapper { filter: drop-shadow(0 0 12px rgba(108, 76, 255, 0.35)); }
    }
  `;
  document.head.appendChild(style);
}
